/* eslint-disable no-return-assign */
import { Alert, AlertTitle, Box } from '@mui/material';
import { useEffect, useRef, useState } from 'react';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  useDataProvider,
  FieldGeneratorV2,
  FormHandler,
  PageLoading,
  useUserAccess,
  useMasterData,
  useEntityPermission,
  useLocalization,
  SnackbarApiErrorMessage,
  useDialog,
  useSnackbar,
} from '@ui-framework/index';
import { Engine } from 'json-rules-engine';
import * as yup from 'yup';
import {
  COMPANY_TYPE,
  HIDE_ADD_CLAIM_BUTTON_WHEN_STATUS,
} from '@constants/app';
import { addFormSchemaEssentials } from './SJFormSchemaHelper';
import CancelJobDialog from '../Dialogs/CancelJobDialog';
import SJFormHelper from './SJFormHelper';
import SJFloatingButtons from './SJFloatingButtons';
import SJHeader from './SJHeader';
import PageSection from '../PageSection';
import ContentStepper from '../ContentStepper';
import RejectJobDialog from '../Dialogs/RejectJobDialog';
import RescheduleJobDialog from '../Dialogs/RescheduleJobDialog';
import ConditionallyAcceptJobDialog from '../Dialogs/ConditionallyAcceptJobDialog';
import AcceptJobDialog from '../Dialogs/AcceptJobDialog';
import UpdateAppointmentDate from '../Dialogs/UpdateAppointmentDateDialog';
import ClaimDetailDialog from '../FormFields/Claims/components/ClaimDetailDialog';
import { useServiceModule } from '../ServiceModuleProvider';

const EXTERNAL_DIALOG = {
  ACCEPT_JOB: 'accept-job',
  REJECT_JOB: 'reject-job',
  CONDITIONALLY_ACCEPT_JOB: 'accept-conditional-job',
  RESCHEDULE_JOB: 'reschedule-job',
  CANCEL_JOB: 'cancel-job',
  ADD_CLAIM: 'add-claim',
};

function SJFormUI({
  formObject,
  urlParams = {},
  customComponents = {},
  action = null,
  serviceJobFormData = null,
}) {
  const { formatMessage } = useLocalization();
  const provider =
    process.env.NODE_ENV === 'development' ? 'sxclServices' : 'default';
  const dataProvider = useDataProvider(provider);
  const { getApiUrl, get } = dataProvider;
  const snackbar = useSnackbar();
  const formRef = useRef();
  const navigate = useNavigate();
  const sectionRefs = useRef({});
  const userAccess = useUserAccess();
  const masterData = useMasterData();
  const engine = new Engine();
  const { getPermission } = useEntityPermission();
  const isSA =
    userAccess.company.companyType === COMPANY_TYPE.SERVICE_ADMINISTRATOR;
  const isAdmin = userAccess.company.companyType === COMPANY_TYPE.ADMIN;
  const { confirmationRef, confirmationSecondaryRef } = useDialog();
  const { moduleSettings } = useServiceModule();

  const [currentPageUrlParams] = useSearchParams();
  let dialogAction = currentPageUrlParams.get('email_action') || null;
  const claimIdFromUrl = currentPageUrlParams.get('claimid') || null;

  const dialogDispatchEventId =
    currentPageUrlParams.get('dispatch_event_id') || null;

  // Dont show dialog for links coming from email
  if (isSA || isAdmin) {
    dialogAction = null;
  }

  const location = useLocation();
  const isRegisterPage =
    location?.pathname?.indexOf('servicejobs/register') !== -1;

  const [actionErrors, setActionErrors] = useState([]);
  const [pageButtons, setPageButtons] = useState([]);
  const [pageSections, setPageSections] = useState([]);
  const [validationSchema, setValidationSchema] = useState(null);
  const [formContext, setFormContext] = useState(formObject?.context);
  const [initialValues, setInitialValues] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [formActionInProgress, setFormActionInProgress] = useState(false);
  const [warnings, setWarnings] = useState([]);
  const [currentPermissions, setCurrentPermissions] = useState({});

  useEffect(() => {
    setDialog(dialogAction);
  }, []);

  const sjFormHelper = new SJFormHelper({
    navigate,
    dataProvider,
    yup,
    formatMessage,
    engine,
    masterData,
  });

  const fetchClaims = async jobId => {
    const response = await get(
      `${getApiUrl()}/sxcl/v1/service-jobs/${jobId}/claims`,
    );
    if (response.ok) {
      return response?.data?.data;
    }
    snackbar.showMessage({
      type: 'error',
      data: <SnackbarApiErrorMessage errors={response?.errors} />,
    });
    return null;
  };

  const showAddClaimButton = async (jobId, currentJobStatus) => {
    const validStatusesToDisplayClaims =
      masterData?.validStatusesToShowClaims || [];
    const validClaimSectionStatuses =
      validStatusesToDisplayClaims.includes(currentJobStatus);

    const notReadOnlyUser = !userAccess.isUserReadonly();

    if (validClaimSectionStatuses && notReadOnlyUser) {
      const claims = await fetchClaims(jobId);
      if (claims) {
        const validAddClaimButtonStatuses =
          !HIDE_ADD_CLAIM_BUTTON_WHEN_STATUS.includes(claims[0]?.claimStatus);

        return validAddClaimButtonStatuses;
      }
    }
    return false;
  };

  const availableParams = {
    companyNumber: userAccess?.company.companyNumber,
    completeJobButtonStatuses: masterData?.completeJobStatuses || [],
    completeServiceButtonStatuses: masterData?.serviceCompleteJobStatuses || [],
    RescheduleButtonStatuses: masterData?.reschedulableJobStatuses || [],
    cancellableButtonStatuses: masterData?.cancellableJobStatuses || [],
    validStatusesToShowClaims: masterData?.validStatusesToShowClaims || [],
  };

  const shownSnackbar = (message, type = null) => {
    snackbar.showMessage({
      ...(type && { type }),
      data: formatMessage({
        id: message.id,
        defaultMessage: message.defaultMessage,
      }),
    });
  };

  const preFlightHookHandler = (apiSection, data) => {
    if (apiSection?.preFlightHook) {
      let requestData = data;
      apiSection.preFlightHook.forEach(hook => {
        switch (hook.type) {
          case 'OBJECT_TO_VALUE': {
            requestData = SJFormHelper.objectToValue({
              obj: requestData,
              attributes: hook.params,
            });

            break;
          }
          case 'RENAME_FIELDS': {
            requestData = SJFormHelper.renameAttributes({
              obj: requestData,
              attributes: hook.params,
            });
            break;
          }
          case 'ONLY_FIELDS': {
            requestData = SJFormHelper.onlyAttributes({
              obj: requestData,
              attributes: hook.params,
            });
            break;
          }
          case 'NO_QUERY_PARAMS': {
            requestData = {};
            break;
          }
          case 'REMOVE_FIELDS': {
            requestData = SJFormHelper.removeAttributes({
              obj: requestData,
              attributes: hook.params,
            });
            break;
          }
          case 'MAKE_BOOLEAN': {
            requestData = SJFormHelper.convertFieldValueToBoolean({
              obj: requestData,
              attributes: hook.params,
            });
            break;
          }
          case 'MAKE_PHONENUMBER_NULL_IF_EMPTY': {
            requestData = SJFormHelper.convertEmptyFieldValueToNull({
              obj: requestData,
              attributes: hook.params,
            });
            break;
          }

          case 'LABEL_VALUE_EXTRACT_VALUE': {
            requestData = SJFormHelper.convertFieldLabelValueToValue({
              obj: requestData,
              attributes: hook.params,
            });
            break;
          }
          default:
        }
      });
      return requestData;
    }
    return data;
  };

  const successHandler = async (successAction, response) => {
    switch (successAction.type) {
      case 'SET_FORM_VALUES': {
        setFormContext(response);
        break;
      }
      case 'REDIRECT': {
        sjFormHelper.navigate({
          url: successAction.url,
          params: response,
        });
        break;
      }

      case 'RULE': {
        const result = await SJFormHelper.runRuleEngine({
          rules: successAction.rules,
          params: {
            ...response,
            serviceJobFormId: response?.serviceJobForm?.id || null,
          },
        });
        if (Array.isArray(result) && result.length > 0) {
          const ruleEngineResult = result[0];
          switch (ruleEngineResult.type) {
            case 'NAVIGATE': {
              sjFormHelper.navigate({
                url: ruleEngineResult.params.url,
                params: response,
              });
              break;
            }
            case 'SET_FORM_VALUES': {
              setFormContext(response);
              break;
            }
            default:
          }
        }
        break;
      }
      default:
    }
  };

  const buttonAPiSubmitHandler = async (data, buttonConfig) => {
    const currentData = [data];
    const responses = await buttonConfig?.api?.reduce(
      async (accResponse, apiSection, index) => {
        let { currentErrors } = await accResponse;

        if (Array.isArray(currentErrors) && currentErrors.length > 0) {
          return { currentData, currentErrors };
        }

        const additionalParams = SJFormHelper.getAdditonalParamsToInject({
          requiredParams: apiSection?.paramsAdditional || [],
          availableParams,
        });

        const requestData = preFlightHookHandler(
          apiSection,
          currentData[index],
        );

        const apiData = {
          ...requestData,
          ...additionalParams,
          recordVersion:
            currentData[index]?.recordVersion ??
            initialValues?.recordVersion ??
            formContext?.recordVersion,
        };

        // Remove fields that were added for rules or other things
        const {
          companyType,
          roleIdentifier,
          completeJobButtonStatuses,
          completeServiceButtonStatuses,
          RescheduleButtonStatuses,
          cancellableButtonStatuses,
          dispatchEventStatus,
          validStatusesToShowClaims,
          allowedToShowClaimsButtons,
          displayAddClaimsButton,
          insightPurchased,
          serviceJobFormData: _serviceJobFormDataRemoved,
          ...finalApiData
        } = apiData;

        const apiResponse = await sjFormHelper.call({
          method: apiSection.method,
          url: apiSection.url,
          params: urlParams,
          data: finalApiData,
        });

        const response = apiResponse?.data || {};

        currentData.push(response);

        if (apiResponse?.ok) {
          let hasServiceJobErrors = false;

          setWarnings(response?.warnings || []);

          if (response?.serviceJobErrors) {
            // TODO: Hack for Service Job Register Save page when api return ServiceJobErrors
            const sjId = response?.id || null;
            const sjNUmber = response?.serviceJobNumber || null;
            const sjFormId = response?.serviceJobForm?.id || null;
            if (isRegisterPage && sjId && sjNUmber && sjFormId) {
              hasServiceJobErrors = false;
            } else {
              hasServiceJobErrors =
                Array.isArray(response?.serviceJobErrors) &&
                response?.serviceJobErrors.length > 0;
            }
          }

          if (!hasServiceJobErrors && apiSection?.successMessage) {
            shownSnackbar(apiSection.successMessage);
          }

          if (!hasServiceJobErrors && apiSection?.onSuccess) {
            apiSection?.onSuccess.forEach(async successAction => {
              await successHandler(successAction, response);
            });
          }

          if (hasServiceJobErrors) {
            if (response?.id) {
              setFormContext(response);
            }
            shownSnackbar(apiSection.errorMessage, 'error');
            currentErrors = [...currentErrors, ...response.serviceJobErrors];
            return {
              currentData:
                Array.isArray(currentData) && currentData.length > 0
                  ? currentData[currentData.length - 1]
                  : currentData,
              currentErrors,
            };
          }

          return {
            currentData:
              Array.isArray(currentData) && currentData.length > 0
                ? currentData[currentData.length - 1]
                : currentData,
            currentErrors,
          };
        }
        shownSnackbar(apiSection.errorMessage, 'error');
        currentErrors = [...currentErrors, ...apiResponse.errors];
        return {
          currentData:
            Array.isArray(currentData) && currentData.length > 0
              ? currentData[currentData.length - 1]
              : currentData,
          currentErrors,
        };
      },
      Promise.resolve({ currentErrors: [] }),
    );
    return responses;
  };

  const processSubmitRequest = async (data, buttonConfig) => {
    const { currentErrors: responseErrors } = await buttonAPiSubmitHandler(
      data,
      buttonConfig,
    );
    if (Array.isArray(responseErrors) && responseErrors.length > 0) {
      setActionErrors(responseErrors);
    }
  };

  async function renderConfirmationDialog({
    config,
    data,
    buttonConfig,
    jobId,
  }) {
    switch (config?.type) {
      case 'JOB_COMPLETE_WITH_NO_CLAIMS': {
        const claims = await fetchClaims(jobId);
        // No Claims Existed
        if (claims.length === 0) {
          const { title, message } = config;
          confirmationSecondaryRef.current.init({
            message:
              message || 'Are you certain you want to perform this action?',
            positiveButtonText: 'Yes',
            negativeButtonText: 'No',
            width: 360,
            title: title || 'Confirmation',
            onChange: async isYes => {
              if (isYes) {
                processSubmitRequest(data, buttonConfig);
              }
              confirmationSecondaryRef.current.setOpen(false);
            },
          });
          confirmationSecondaryRef.current.setOpen(true);
        } else {
          processSubmitRequest(data, buttonConfig);
        }
        break;
      }
      default:
    }
  }

  const saveServiceJobForm = async ({ data }) => {
    const apiSection = {
      api: [
        {
          url: '/sxcl/v1/service-jobs/{id}',
          method: 'PUT',
          stopOnError: true,
          errorMessage: {
            id: 'SERVICE_JOB_SAVE_ERROR',
            defaultMessage: 'Job could not be saved',
          },
          preFlightHook: [
            {
              type: 'REMOVE_FIELDS',
              params: [
                'id',
                'serviceJobNumber',
                'serviceAdministrator',
                'serviceJobStatus',
                'serviceJobForm',
                'serviceJobErrors',
                'industryType',
                'productType',
                'serviceAdministratorClient',
                'serviceSiteCode',
                'serviceTypeCode',
                'warrantyTypeCode',
                'isJobDispatchable',
                'dispatch',
                'createdByUserId',
                'lastModifiedByUserId',
                'serviceJobFormId',
              ],
            },
            {
              type: 'MAKE_BOOLEAN',
              params: ['skipModelSerialCheck'],
            },
            {
              type: 'OBJECT_TO_VALUE',
              params: [{ customerPlaceId: 'id' }],
            },
            {
              type: 'LABEL_VALUE_EXTRACT_VALUE',
              params: [
                'customerAddressState',
                'customerAddressCountry',
                'brand',
                'purchasedFrom',
                'purchasedFromLocation',
                'repairSeverityCode',
              ],
            },
          ],
          paramsAdditional: [],
        },
      ],
    };

    const sjResponseData = await buttonAPiSubmitHandler(data, apiSection);
    return sjResponseData;
  };

  const submitForm = async (data, buttonConfig) => {
    setFormActionInProgress(true);
    switch (buttonConfig.type) {
      case 'API_SUBMIT': {
        if (buttonConfig.confirmation) {
          await renderConfirmationDialog({
            config: buttonConfig.confirmation,
            data,
            buttonConfig,
            jobId: initialValues?.id,
          });
        } else {
          processSubmitRequest(data, buttonConfig);
        }
        break;
      }
      case 'DIALOG_SUBMIT': {
        const responseErrors = await buttonAPiSubmitHandler(data, buttonConfig);
        if (Array.isArray(responseErrors) && responseErrors.length > 0) {
          setActionErrors(responseErrors);
          break;
        } else if (buttonConfig.dialogType) {
          setDialog(EXTERNAL_DIALOG[buttonConfig.dialogType]);
        }

        break;
      }
      case 'NAVIGATE': {
        sjFormHelper.navigate({
          url: buttonConfig.navigate,
          params: initialValues,
        });
        break;
      }
      default:
        formRef.current.reset();
    }
    setFormActionInProgress(false);
  };

  const preProcessSubmitData = data => {
    let processedData = data;

    if (data?.customerServiceRequests) {
      // Convert inital to array incase it was null
      const initialCSR = Array.isArray(initialValues?.customerServiceRequests)
        ? initialValues?.customerServiceRequests
        : [];

      let newCSR = data?.customerServiceRequests;

      if (
        Array.isArray(data?.customerServiceRequests) &&
        data?.customerServiceRequests?.length === 1 &&
        !data?.customerServiceRequests?.[0]?.requestDate
      ) {
        newCSR = [];
      }

      // If there was no change
      if (JSON.stringify(initialCSR) === JSON.stringify(newCSR)) {
        const { customerServiceRequests, ...rest } = data;
        processedData = rest;
      } else {
        processedData = {
          ...data,
          customerServiceRequests: newCSR,
        };
      }
    }
    return processedData;
  };

  const submitFormWithDirtyCheck = async (submittedData, buttonConfig) => {
    const data = preProcessSubmitData(submittedData);
    if (Object.entries(data)?.length > 0 && buttonConfig.checkIfDirty) {
      confirmationRef.current.init({
        message:
          'You have unsaved changes on the job. Do you want to save the changes before proceeding?',
        positiveButtonText: 'Yes',
        negativeButtonText: 'No',
        width: 360,
        title: 'Unsaved Changes',
        onChange: async isYes => {
          if (isYes) {
            const { currentErrors: responseErrors, currentData } =
              await saveServiceJobForm({ data });

            if (Array.isArray(responseErrors) && responseErrors.length > 0) {
              setActionErrors(responseErrors);
            } else {
              const permissions = await getPermission({
                name: 'SERVICEJOB',
                status: currentData?.serviceJobStatus || 'INCOMPLETE',
              });
              const initialValuesFound = sjFormHelper.getFormInitialValues({
                formSchema: formObject?.schema,
                data: currentData,
                customComponents,
                permissions,
              });
              setInitialValues(prev => ({
                ...prev,
                ...initialValuesFound,
              }));
              await submitForm(currentData, buttonConfig);
            }
          } else {
            formRef.current.reset();
            // When api calls dont have form data
            // but api still needs some params
            const fixedValues = {
              recordVersion: initialValues.recordVersion,
            };
            const newData = data?.recordVersion
              ? data
              : { ...data, ...fixedValues };
            await submitForm(newData, buttonConfig);
          }
          confirmationRef.current.setOpen(false);
        },
      });
      confirmationRef.current.setOpen(true);
    } else if (Object.entries(data).length > 0) {
      await submitForm(data, buttonConfig);
    } else {
      // When api calls dont have form data
      // but api still needs some params
      const fixedValues = {
        recordVersion: initialValues.recordVersion,
      };
      await submitForm(fixedValues, buttonConfig);
    }
  };

  const initialize = async formData => {
    setLoading(true);

    let permissions = {};

    if (action !== 'CREATE') {
      permissions = await getPermission({
        name: 'SERVICEJOB',
        status: formData?.serviceJobStatus || 'INCOMPLETE',
      });
    }

    const displayAddClaimsButton = await showAddClaimButton(
      urlParams?.id,
      formData?.serviceJobStatus,
    );

    /*
    customerPlaceSessionToken - This is registered withoput form schema so need to pass it here
    */

    const valuesToPassToForm = {
      ...formData,
      companyType: userAccess.company.companyType,
      serviceJobFormData,
      insightPurchased: moduleSettings?.insightPurchased,
      customerPlaceSessionToken: '',
    };

    const updatedFormObject = JSON.parse(JSON.stringify(formObject));

    const initialValuesFound = sjFormHelper.getFormInitialValues({
      formSchema: updatedFormObject?.schema,
      data: valuesToPassToForm,
      customComponents,
      permissions,
    });

    updatedFormObject.schema.PAGESECTIONS = addFormSchemaEssentials({
      schema: updatedFormObject?.schema.PAGESECTIONS,
      permissions,
      userAccess,
      initialValues: initialValuesFound,
    });

    const valuesToPassToPageItems = {
      ...formData,
      companyType: userAccess.company.companyType,
      roleIdentifier: userAccess?.role?.roleIdentifier,
      completeJobButtonStatuses: availableParams.completeJobButtonStatuses,
      completeServiceButtonStatuses:
        availableParams.completeServiceButtonStatuses,
      RescheduleButtonStatuses: availableParams.RescheduleButtonStatuses,
      cancellableButtonStatuses: availableParams.cancellableButtonStatuses,
      validStatusesToShowClaims: availableParams.validStatusesToShowClaims,
      dispatchEventStatus: formData?.dispatchEvent?.status || null,
      displayAddClaimsButton,
    };

    const pageButtonsFound = await SJFormHelper.getPageButtons(
      updatedFormObject?.schema?.BUTTONS,
      valuesToPassToPageItems,
    );

    const pageSectionsFound = await SJFormHelper.getPageSections(
      updatedFormObject?.schema?.PAGESECTIONS,
      valuesToPassToPageItems,
    );

    const validationSchemaFound = sjFormHelper.getValidationObjects({
      formSchema: {
        ...updatedFormObject?.schema,
        PAGESECTIONS: pageSectionsFound,
      },
    });

    setPageButtons(pageButtonsFound);
    setPageSections(pageSectionsFound);
    setValidationSchema(validationSchemaFound);
    setWarnings(formData?.warnings || []);
    setInitialValues(initialValuesFound);
    setCurrentPermissions(permissions);
    setLoading(false);
  };

  const forceInitializeForm = async () => {
    setFormActionInProgress(true);
    const serviceJobId = initialValues?.id;
    if (serviceJobId) {
      const allServiceJobDetailUrl = `/sxcl/v1/service-jobs/${serviceJobId}`;
      const apiResponse = await sjFormHelper.call({
        method: 'GET',
        url: allServiceJobDetailUrl,
        params: urlParams,
      });

      if (apiResponse?.ok) {
        initialize(apiResponse?.data);
      } else {
        shownSnackbar('Error getting job information', 'error');
      }
    }
    setFormActionInProgress(false);
  };

  useEffect(() => {
    initialize(formContext);
  }, [JSON.stringify(formContext)]);

  if (loading) {
    return (
      <Grid2>
        <PageLoading>Loading</PageLoading>
      </Grid2>
    );
  }

  const onReadyRenderFormItems = () => {
    const items = [];
    if (claimIdFromUrl) {
      items.push(
        <ClaimDetailDialog
          claimId={claimIdFromUrl}
          serviceJobId={initialValues?.id}
          open
          onClose={() => {
            navigate(`/servicejobs/edit/${initialValues?.id}`);
            setDialog(null);
          }}
          serviceJobStatus={initialValues?.serviceJobStatus}
          serviceJobFormData={serviceJobFormData}
        />,
      );
    }
    return items;
  };

  function dialogOnClose(success, externalDialog) {
    const saRedirectUrl = `/servicejobs/edit/${initialValues?.id}`;
    const spRedirectUrl = `/servicejobs/serviceprovider/${userAccess?.company?.id}/${initialValues?.id}/pendingacceptancejob?`;
    if (success) {
      if (
        externalDialog === EXTERNAL_DIALOG.ACCEPT_JOB ||
        externalDialog === EXTERNAL_DIALOG.ADD_CLAIM
      ) {
        window.location.href = saRedirectUrl;
      } else {
        window.location.href =
          userAccess.isSA() || userAccess.isAdmin()
            ? saRedirectUrl
            : spRedirectUrl;
      }
    }
    setDialog(null);
  }

  return (
    <Box>
      <FormHandler
        formSchema={pageSections}
        sendDirtyOnly
        disableUnSavedCheck
        ref={formRef}
        validationSchema={validationSchema || {}}
        onSubmit={(data, buttonConfig) =>
          submitFormWithDirtyCheck(data, buttonConfig)
        }
        initialValues={initialValues}
        onReadyRenderFormItems={() => onReadyRenderFormItems()}>
        {formActionInProgress && (
          <Grid2>
            <PageLoading>Processing...</PageLoading>
          </Grid2>
        )}

        {!formActionInProgress && actionErrors && actionErrors.length > 0 && (
          <Alert sx={{ mb: 2, mt: -1 }} severity="error">
            <AlertTitle>
              {formatMessage({
                id: 'FORM_ERRORS',
                defaultMessage: 'Form Errors',
              })}
            </AlertTitle>
            <ul>
              {actionErrors.map(actionError => (
                <li key={actionError?.message}>{actionError?.message}</li>
              ))}
            </ul>
          </Alert>
        )}
        {!formActionInProgress && warnings && warnings.length > 0 && (
          <Alert sx={{ mb: 2, mt: -1 }} severity="warning">
            <AlertTitle>
              {formatMessage({
                id: 'FORM_WRNINGS',
                defaultMessage: 'Warnings',
              })}
            </AlertTitle>
            <ul>
              {warnings.map(warningObj => (
                <li key={warningObj?.message}>{warningObj?.message}</li>
              ))}
            </ul>
          </Alert>
        )}

        {!formActionInProgress && pageSections.length > 0 && (
          <ContentStepper
            header={
              <SJHeader
                formData={initialValues}
                sx={{ marginBottom: '10px' }}
              />
            }
            sectionRefs={sectionRefs}
            pageSections={pageSections}
            formObject={formObject}>
            {pageSections.map(formItem => (
              <PageSection
                key={formItem?.locale?.id}
                ref={el => (sectionRefs.current[formItem.locale.id] = el)}
                id={formItem.locale.id}
                label={formatMessage({
                  id: formItem.locale.id,
                  defaultMessage: formItem.locale.defaultMessage,
                })}>
                <FieldGeneratorV2
                  customComponents={customComponents}
                  schema={formItem.children}
                  permissions={currentPermissions || {}}
                  forceInitializeForm={forceInitializeForm}
                />
              </PageSection>
            ))}
          </ContentStepper>
        )}
      </FormHandler>

      {!formActionInProgress && (
        <SJFloatingButtons
          position={{ bottom: true, right: true }}
          buttonsSchema={pageButtons}
          formRef={formRef}
          preSubmitActions={() => {
            setActionErrors([]);
            setWarnings([]);
          }}
        />
      )}

      {dialog === EXTERNAL_DIALOG.ACCEPT_JOB && (
        <AcceptJobDialog
          key={dialog === EXTERNAL_DIALOG.ACCEPT_JOB ? `AJD1` : `AJD2`}
          open={dialog === EXTERNAL_DIALOG.ACCEPT_JOB}
          onClose={success => {
            dialogOnClose(success, EXTERNAL_DIALOG.ACCEPT_JOB);
          }}
          jobInfo={initialValues}
          dispatchEventId={dialogDispatchEventId || null}
        />
      )}

      {dialog === EXTERNAL_DIALOG.REJECT_JOB && (
        <RejectJobDialog
          key={dialog === EXTERNAL_DIALOG.REJECT_JOB ? `RJB1` : `RJB2`}
          open={dialog === EXTERNAL_DIALOG.REJECT_JOB}
          onClose={success => {
            dialogOnClose(success);
          }}
          jobId={initialValues?.id}
          dispatchEventId={dialogDispatchEventId || null}
          jobNumber={initialValues?.serviceJobNumber}
        />
      )}

      {dialog === EXTERNAL_DIALOG.CONDITIONALLY_ACCEPT_JOB && (
        <ConditionallyAcceptJobDialog
          key={
            dialog === EXTERNAL_DIALOG.CONDITIONALLY_ACCEPT_JOB
              ? `CAJD1`
              : `CAJD2`
          }
          open={dialog === EXTERNAL_DIALOG.CONDITIONALLY_ACCEPT_JOB}
          onClose={success => {
            dialogOnClose(success);
          }}
          jobInfo={initialValues}
        />
      )}

      {(isSA || isAdmin) && dialog === EXTERNAL_DIALOG.RESCHEDULE_JOB && (
        <RescheduleJobDialog
          key={dialog === EXTERNAL_DIALOG.RESCHEDULE_JOB ? `REJD1` : `REJD2`}
          open={dialog === EXTERNAL_DIALOG.RESCHEDULE_JOB}
          onClose={success => {
            dialogOnClose(success);
          }}
          initialData={initialValues}
          recordVersion={initialValues?.recordVersion}
          jobId={initialValues?.id}
          jobNumber={initialValues?.serviceJobNumber}
          jobStatus={initialValues?.serviceJobStatus}
          providerId={initialValues?.serviceProviderId}
          permissions={{
            customerServiceRequests: {
              requestDate: 'WRITE',
              afterHour: 'WRITE',
              beforeHour: 'WRITE',
            },
          }}
        />
      )}

      {!isSA && !isAdmin && dialog === EXTERNAL_DIALOG.RESCHEDULE_JOB && (
        <UpdateAppointmentDate
          key={dialog === EXTERNAL_DIALOG.RESCHEDULE_JOB ? `UAD1` : `UAD2`}
          open={dialog === EXTERNAL_DIALOG.RESCHEDULE_JOB}
          onClose={success => {
            dialogOnClose(success);
          }}
          jobInfo={initialValues}
        />
      )}

      {dialog === EXTERNAL_DIALOG.CANCEL_JOB && (
        <CancelJobDialog
          key={dialog === EXTERNAL_DIALOG.CANCEL_JOB ? `CJD1` : `CJD2`}
          open={dialog === EXTERNAL_DIALOG.CANCEL_JOB}
          onClose={success => {
            dialogOnClose(success);
          }}
          recordVersion={initialValues?.recordVersion}
          jobId={initialValues?.id}
          jobNumber={initialValues?.serviceJobNumber}
          jobStatus={initialValues?.serviceJobStatus}
        />
      )}

      {dialog === EXTERNAL_DIALOG.ADD_CLAIM && (
        <ClaimDetailDialog
          key={dialog === EXTERNAL_DIALOG.ADD_CLAIM ? `ACD1` : `ACD2`}
          open={dialog === EXTERNAL_DIALOG.ADD_CLAIM}
          serviceJobId={initialValues?.id}
          claimId={null}
          onClose={success => {
            dialogOnClose(success, EXTERNAL_DIALOG.ADD_CLAIM);
          }}
          serviceJobFormData={serviceJobFormData}
        />
      )}
    </Box>
  );
}

export default SJFormUI;
